"use client";

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Breadcrumb,
  Tooltip,
  Typography,
  Modal,
  message,
} from "antd";
import {
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import { getDefaultPagination } from "../utils/paginationConfig";
import {
  JobConfig,
  JobPendingComponentProps,
  JobRun,
} from "../models/interfaces";
import { JobType } from "../constants/JobTypes";
import mixpanel from "mixpanel-browser";
import { Events } from "../constants/Event";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const { Text } = Typography;

const JobConfigView: React.FC<JobPendingComponentProps> = ({
  jobConfigList,
  jobRunList,
  breadCrumbs,
  tableColumns,
  jobType,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [loadingJobs, setLoadingJobs] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [deletingJobs, setDeletingJobs] = useState<{ [key: string]: boolean }>(
    {}
  );

  const onExecuteClick = async (jobConfigId: string) => {
    setLoadingJobs((prev) => ({ ...prev, [jobConfigId]: true }));
    try {
      await axiosInstance.post(
        "/api/jobRun",
        { jobConfigId },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        }
      );
      mixpanel.track(Events.BUTTON_CLICK, {
        jobType,
        action: "Execute",
        jobConfigId,
      });
      message.success("Test execution started. It has been added to the queue.");
      if (onRefresh) {
        onRefresh();
      } else {
        window.location.reload();
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
        "Failed to start execution. Please try again."
      );
    } finally {
      setLoadingJobs((prev) => ({ ...prev, [jobConfigId]: false }));
    }
  };

  const onDeleteClick = async (jobConfigId: string, jobName: string) => {
    Modal.confirm({
      title: "Delete Job Configuration",
      content: `Are you sure you want to delete "${jobName}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeletingJobs((prev) => ({ ...prev, [jobConfigId]: true }));

          await axiosInstance.delete(`/api/jobConfig/${jobConfigId}`, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          });

          mixpanel.track(Events.BUTTON_CLICK, {
            jobType,
            action: "Delete",
            jobConfigId,
          });

          message.success(`Successfully deleted "${jobName}"`);

          // Reload the page to refresh the job list after a short delay to show the message
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error: any) {
          console.error("Error deleting job:", error);
          message.error(
            error?.response?.data?.message ||
            "Failed to delete job configuration. Please try again."
          );
          setDeletingJobs((prev) => ({ ...prev, [jobConfigId]: false }));
        }
      },
    });
  };


  const handleViewClick = (
    jobType: JobType,
    jobConfigId: string,
    jobName: string
  ) => {
    if (jobType === JobType.MarketingMixModel) {
      navigate(`/jobs/MMM`, {
        state: {
          jobConfigId,
          jobName,
          step: 0,
          viewOnly: true,
        },
      });
      // navigate(`/planning/marketing-mix-model/${jobConfigId}?name=${jobName}`);
    }
    if (jobType === JobType.BidOptimizer) {
      navigate(`/jobs/BidOptimizer`, {
        state: {
          jobConfigId,
          jobName,
          step: 3,
        },
      });
      // navigate(`/optimization/bid-optimizer/${jobConfigId}?name=${jobName}`);
    }
    if (jobType === JobType.GeoLift) {
      navigate(`/experiments/geo-lift/${jobConfigId}?name=${jobName}`);
    }
    mixpanel.track(Events.BUTTON_CLICK, {
      jobType,
      action: "View",
      jobConfigId,
    });
  };

  const getJobAction = (jobConfig: JobConfig, jobRunList: JobRun[]) => {
    const jobRun = jobRunList.filter(
      (jobRun) => jobRun.jobConfigId === jobConfig.jobConfigId
    );
    const tainentId = jobRunList.find(
      (jobRun) => jobRun.jobConfigId === jobConfig.jobConfigId
    )?.tenantId;
    console.log("job list", tainentId);

    const isDeleting = deletingJobs[jobConfig.jobConfigId];

    // console.log('job run list',jobRun)

    if (jobRun && jobRun.length === 1) {
      const job = jobRun[0];
      const executionTime = dayjs.utc(job.jobExecutionTime); // Ensure UTC
      const now = dayjs.utc(); // Also in UTC
      const isStuck =
        (job.jobStatus === "RUNNING" || job.jobStatus === "PENDING") &&
        now.diff(executionTime, "hour") >= 1;
      if (isStuck) {
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <Tooltip title="Please check the upload data for the test and contact Adrsta support for help.">
              <div className="flex items-center gap-2 text-red-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>Failed</span>
              </div>
            </Tooltip>
            <Tooltip title="Delete job configuration">
              <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onDeleteClick(jobConfig.jobConfigId, jobConfig.name)}
                loading={isDeleting}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </Tooltip>
          </div>
        );
      }
      if (jobRun[0].jobStatus === "RUNNING") {
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
              <span>Running</span>
            </div>
            <Tooltip title="Delete job configuration">
              <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onDeleteClick(jobConfig.jobConfigId, jobConfig.name)}
                loading={isDeleting}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </Tooltip>
          </div>
        );
      } else if (jobRun[0].jobStatus === "PENDING") {
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"></div>
              </div>
              <span>In Queue</span>
            </div>
            <Tooltip title="Delete job configuration">
              <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onDeleteClick(jobConfig.jobConfigId, jobConfig.name)}
                loading={isDeleting}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </Tooltip>
          </div>
        );
      }
    }
    if (jobRun && jobRun.length === 1 && jobRun[0].jobStatus === "FAILED") {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span>Failed</span>
          </div>
          <Tooltip title="Delete job configuration">
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDeleteClick(jobConfig.jobConfigId, jobConfig.name)}
              loading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      );
    } else if (jobRun?.length >= 1 && jobRun[0].jobStatus === "SUCCESSFUL") {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() =>
              handleViewClick(
                jobRun[0].jobType,
                jobRun[0].jobConfigId,
                jobConfig.name
              )
            }
          >
            {jobRun[0].jobType === JobType.GeoLift
              ? "View Synthetic Control"
              : "View"}
          </Button>
          <Tooltip title="Delete job configuration">
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDeleteClick(jobConfig.jobConfigId, jobConfig.name)}
              loading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => onExecuteClick(jobConfig.jobConfigId)}
            loading={loadingJobs[jobConfig.jobConfigId]}
          >
            {loadingJobs[jobConfig.jobConfigId] ? "Executing..." : "Execute"}
          </Button>
          <Tooltip title="Delete job configuration">
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDeleteClick(jobConfig.jobConfigId, jobConfig.name)}
              loading={isDeleting}
              disabled={isDeleting || loadingJobs[jobConfig.jobConfigId]}
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      );
    }
  };
  function formatDateToShortMonth(dateString: any): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  }
  // Transform the data for Ant Design Table
  const dataSource = [...jobConfigList]
    ?.sort((a, b) => {
      return (
        new Date(b.createdDate as string).getTime() -
        new Date(a.createdDate as string).getTime()
      );
    })
    .map((jobConfig, index) => {
      return {
        key: index,
        name: jobConfig.name,
        createdDate: formatDateToShortMonth(jobConfig.createdDate),
        action: getJobAction(jobConfig, jobRunList),
        jobConfigId: jobConfig.jobConfigId,
        jobConfig: jobConfig,
      };
    });

  // Transform columns for Ant Design Table
  const antColumns = [
    ...tableColumns.map((column: any) => ({
      title: column.label as string,
      dataIndex: column.key,
      key: column.key,
    })),
  ];

  return (
    <div className="p-5">
      <div className="flex justify-between items-center">
        <Breadcrumb>
          {breadCrumbs.map((entity: any, index: number) => (
            <Breadcrumb.Item key={index}>
              <Link to={entity.url}>{entity.name}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <div className="flex justify-end items-center gap-2">
          <Button type="primary" onClick={() => navigate(`/jobs/${jobType}`)}>
            {jobType === JobType.BidOptimizer
              ? "Run Bid Optimizer Test"
              : jobType === JobType.MarketingMixModel
              ? "Run MMM Test"
              : jobType === JobType.NextBestAction
              ? "Run Next Best Action"
              : "Create New Study"}
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => onRefresh ? onRefresh() : window.location.reload()}
          >
            Refresh
          </Button>
        </div>
      </div>
      {dataSource && (
        <div className="mt-6">
          <Table
            dataSource={dataSource}
            columns={antColumns}
            pagination={getDefaultPagination()}
            bordered
            scroll={{ x: "max-content" }}
          />
        </div>
      )}
    </div>
  );
};

export default JobConfigView;
